import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import Layout from '../components/Layout'
import { api } from '../lib/api'
import { Card, ErrorMessage, Field, Spinner } from '../components/ui'

// The admin API answers 404 to a non-admin rather than 403, so that a signed-in
// stranger poking at /admin learns nothing. That means this page cannot tell
// "you may not" from "no such thing" — and should not try. One honest message
// covers both.
function NotAvailable() {
  return (
    <Card title="Not available">
      <p className="text-sm text-slate-400">
        This area is not available on your account.
      </p>
    </Card>
  )
}

export default function Admin() {
  const [query, setQuery] = useState('')

  const users = useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => api.adminUsers(query),
    retry: false,
  })

  return (
    <Layout wide>
      <div className="space-y-4">
        <h1 className="text-lg font-semibold text-slate-100">Users</h1>

        <Card>
          <Field
            label="Search by email"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="someone@example.com"
            autoComplete="off"
          />
        </Card>

        {users.isLoading && <Spinner />}
        {users.error?.status === 404 ? (
          <NotAvailable />
        ) : (
          <ErrorMessage error={users.error} />
        )}

        {users.data && (
          <Card>
            {users.data.users.length === 0 ? (
              <p className="text-sm text-slate-400">No users match that.</p>
            ) : (
              <ul className="divide-y divide-slate-800">
                {users.data.users.map((user) => (
                  <li key={user.id} className="py-2">
                    <Link
                      to={`/admin/users/${user.id}`}
                      className="flex flex-wrap items-center justify-between gap-2 hover:opacity-80"
                    >
                      <span className="text-sm text-slate-100">{user.email_address}</span>
                      <span className="flex items-center gap-2 text-xs">
                        {user.admin && <Badge tone="amber">admin</Badge>}
                        {user.comped && <Badge tone="emerald">comped</Badge>}
                        {user.free_evaluation_limit_override !== null && (
                          <Badge tone="sky">limit {user.free_evaluation_limit_override}</Badge>
                        )}
                        <span className="text-slate-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {users.data.truncated && (
              <p className="mt-3 text-xs text-slate-500">
                Showing the newest matches only — narrow the search to see more.
              </p>
            )}
          </Card>
        )}
      </div>
    </Layout>
  )
}

export function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-700 text-slate-300',
    amber: 'border-amber-800 text-amber-300',
    emerald: 'border-emerald-800 text-emerald-300',
    sky: 'border-sky-800 text-sky-300',
  }

  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] ${tones[tone]}`}>
      {children}
    </span>
  )
}
